import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import { useSetTrail } from "../context/BreadcrumbContext";
import { api, notify } from "../lib/api";
import { timeAgo } from "../lib/time";
import type { AdminPlayerSummary } from "../types/domain";

const PAGE_SIZE = 20;
const SKELETON_ROWS = 5;

interface EditState {
  pseudo: string;
  role: "player" | "admin";
}

export function AdminPage() {
  const { user } = useAuth();
  const [players, setPlayers] = useState<AdminPlayerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  useSetTrail([]);

  useEffect(() => {
    void api.listAdminPlayers().then((data) => {
      setPlayers(data);
      setLoading(false);
    });
  }, []);

  const totalPages = Math.max(1, Math.ceil(players.length / PAGE_SIZE));
  const safeP = Math.min(page, totalPages);
  const visible = players.slice((safeP - 1) * PAGE_SIZE, safeP * PAGE_SIZE);

  function startEdit(player: AdminPlayerSummary) {
    setEditingId(player.id);
    setEditState({ pseudo: player.pseudo ?? "", role: player.role });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditState(null);
  }

  async function saveEdit(playerId: string) {
    if (!editState) return;
    const trimmedPseudo = editState.pseudo.trim();
    if (trimmedPseudo.length > 0 && trimmedPseudo.length < 2) {
      notify("error", "Le pseudo doit contenir au moins 2 caractères");
      return;
    }
    setSaving(true);
    try {
      const original = players.find((p) => p.id === playerId);
      const payload: { pseudo?: string; role?: "player" | "admin" } = {};
      if (
        trimmedPseudo.length >= 2 &&
        trimmedPseudo !== (original?.pseudo ?? "")
      )
        payload.pseudo = trimmedPseudo;
      if (editState.role !== original?.role) payload.role = editState.role;
      const updated = await api.adminUpdatePlayer(playerId, payload);
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId
            ? { ...p, ...updated, lastSeenAt: p.lastSeenAt }
            : p,
        ),
      );
      setEditingId(null);
      setEditState(null);
      notify("success", "Utilisateur mis à jour");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-ink">Utilisateurs</h1>
        <Link className="btn-primary text-center" to="/admin/users/new">
          Ajouter un utilisateur
        </Link>
      </div>

      <div className="sm:overflow-x-auto sm:rounded-lg sm:border sm:border-gray-100">
        <table className="block w-full text-sm sm:table sm:table-fixed">
          <thead className="hidden sm:table-header-group">
            <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-smoke">
              <th className="w-36 px-4 py-3">Pseudo</th>
              <th className="px-4 py-3">Email</th>
              <th className="w-28 px-4 py-3">Rôle</th>
              <th className="w-28 px-4 py-3">Dernière connexion</th>
              <th className="w-32 px-4 py-3 text-right" />
            </tr>
          </thead>
          <tbody className="grid gap-3 sm:table-row-group sm:divide-y sm:divide-gray-100">
            {loading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <tr
                    key={i}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 rounded-lg border border-gray-100 p-4 shadow-sm sm:table-row sm:border-0 sm:p-0 sm:shadow-none"
                  >
                    <td className="sm:table-cell sm:px-4 sm:py-3">
                      <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="col-span-2 sm:table-cell sm:px-4 sm:py-3">
                      <div className="h-3 w-36 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="col-start-2 row-start-1 sm:table-cell sm:px-4 sm:py-3">
                      <div className="h-4 w-12 animate-pulse rounded-full bg-gray-200" />
                    </td>
                    <td className="sm:table-cell sm:px-4 sm:py-3">
                      <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
                    </td>
                    <td className="hidden sm:table-cell sm:px-4 sm:py-3" />
                  </tr>
                ))
              : visible.map((player) => {
                  const isEditing = editingId === player.id;
                  const isOwnRow = player.id === user?.id;

                  if (isEditing && editState) {
                    return (
                      <tr
                        key={player.id}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 rounded-lg border border-gray-100 bg-gray-50 p-4 shadow-sm sm:table-row sm:border-0 sm:p-0 sm:shadow-none"
                      >
                        <td className="col-span-2 sm:table-cell sm:px-4 sm:py-2">
                          <input
                            type="text"
                            autoFocus
                            placeholder="Pseudo"
                            className="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm text-ink outline-none focus:border-ember focus:ring-1 focus:ring-ember/20"
                            value={editState.pseudo}
                            onChange={(e) =>
                              setEditState(
                                (s) => s && { ...s, pseudo: e.target.value },
                              )
                            }
                          />
                        </td>
                        <td className="col-span-2 min-w-0 break-all text-smoke sm:table-cell sm:break-normal sm:px-4 sm:py-2">
                          {player.email}
                        </td>
                        <td className="col-span-2 sm:table-cell sm:px-4 sm:py-2">
                          <div className="flex gap-1">
                            {(["player", "admin"] as const).map((r) => (
                              <button
                                key={r}
                                type="button"
                                disabled={isOwnRow}
                                onClick={() =>
                                  setEditState((s) => s && { ...s, role: r })
                                }
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold transition ${
                                  editState.role === r
                                    ? r === "admin"
                                      ? "bg-ember/10 text-ember"
                                      : "bg-gray-200 text-ink"
                                    : "bg-gray-100 text-smoke hover:bg-gray-200"
                                } disabled:cursor-not-allowed disabled:opacity-40`}
                              >
                                {r === "admin" ? "admin" : "joueur"}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className="col-start-1 row-start-4 text-xs text-smoke sm:table-cell sm:px-4 sm:py-2 sm:text-sm">
                          <span className="sm:hidden">
                            Dernière connexion :{" "}
                          </span>
                          {timeAgo(player.lastSeenAt)}
                        </td>
                        <td className="col-start-2 row-start-4 text-right sm:table-cell sm:px-4 sm:py-2">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              className="text-xs font-medium text-ember hover:text-ember/80 disabled:opacity-50"
                              disabled={saving}
                              onClick={() => saveEdit(player.id)}
                            >
                              Enregistrer
                            </button>
                            <button
                              type="button"
                              className="text-xs text-smoke hover:text-ink"
                              disabled={saving}
                              onClick={cancelEdit}
                            >
                              Annuler
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={player.id}
                      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-lg border border-gray-100 p-4 shadow-sm sm:table-row sm:border-0 sm:p-0 sm:shadow-none ${
                        player.status === "pending_profile"
                          ? "text-smoke"
                          : "text-ink"
                      }`}
                    >
                      <td className="min-w-0 font-medium sm:table-cell sm:px-4 sm:py-3">
                        {player.pseudo ?? "-"}
                      </td>
                      <td className="col-span-2 min-w-0 break-all text-smoke sm:table-cell sm:break-normal sm:px-4 sm:py-3 sm:text-inherit">
                        {player.email}
                      </td>
                      <td className="col-start-2 row-start-1 sm:table-cell sm:px-4 sm:py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                            player.role === "admin"
                              ? "bg-ember/10 text-ember"
                              : "bg-gray-100 text-smoke"
                          }`}
                        >
                          {player.role === "admin" ? "admin" : "joueur"}
                        </span>
                      </td>
                      <td className="col-start-1 row-start-3 text-xs text-smoke sm:table-cell sm:px-4 sm:py-3 sm:text-sm">
                        <span className="sm:hidden">Dernière connexion : </span>
                        {timeAgo(player.lastSeenAt)}
                      </td>
                      <td className="col-start-2 row-start-3 text-right sm:table-cell sm:px-4 sm:py-3">
                        <button
                          type="button"
                          className="text-xs text-smoke hover:text-ink"
                          onClick={() => startEdit(player)}
                        >
                          Modifier
                        </button>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination
          page={safeP}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
